import moment from 'moment';
import axios, { isAxiosError } from 'axios';
import swot from 'swot-simple';
import { JSONValue } from './interface';
import { ProxyLink } from './constants';

export const formatDate = (date: Date | string): string => {
	return moment(date).format('YYYY-MM-DD');
};

export const extractVideoId = (url: string): string | null => {
	const regExp =
		/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
	const match = url.match(regExp);
	return match ? match[1] : null;
};

export const maskEmail = (email: string): string => {
	const [username, domain] = email.split('@');
	return `${username.slice(0, 2)}***${username.slice(-2)}@${domain}`;
};

// Phone number validation function for Malaysian mobile providers
export const validateMalaysianMobileNumber = (_: any, value: string) => {
	if (!value) {
		return Promise.resolve();
	}

	// Remove any non-digit characters
	const cleanValue = value.replace(/\D/g, '');

	// Extract first 2 digits
	const firstTwoDigits = cleanValue.substring(0, 2);

	// Check provider-specific rules
	if (firstTwoDigits === '11' || firstTwoDigits === '15') {
		// For 11 or 15, remaining digits must be 8
		if (cleanValue.length !== 10) {
			return Promise.reject(new Error('Invalid Malaysian mobile number format'));
		}
	} else {
		// For other providers, remaining digits must be 7
		if (cleanValue.length !== 9) {
			return Promise.reject(new Error('Invalid Malaysian mobile number format'));
		}
	}

	return Promise.resolve();
};

export const validateStudentEmail = async (_: any, value: string) => {
	if (!value) {
		return;
	}

	var isAcademic = await swot.isAcademic(value);
	//need to check student in whole email as well
	if (!isAcademic) {
		isAcademic = /student/i.test(value);
	}
	const isStaff = value.toLowerCase().endsWith('@tarc.edu.my');

	if (isAcademic && !isStaff) {
		return Promise.reject(
			new Error('Academic email addresses are not allowed. Please use an alternative email address')
		);
	}
};

export const validateICDate = (_: any, value: string) => {
	if (!value) {
		return Promise.resolve();
	}

	const datePart = value.split('-')[0];
	if (datePart.length !== 6) {
		// Let the regex pattern handle length/format errors
		return Promise.resolve();
	}

	const yy = datePart.substring(0, 2);
	const mm = datePart.substring(2, 4);
	const dd = datePart.substring(4, 6);

	const currentYear = moment().year();
	const currentYearShort = currentYear % 100;
	const century = parseInt(yy, 10) > currentYearShort ? '19' : '20';
	const fullYear = `${century}${yy}`;

	const isValidDate = moment(`${fullYear}-${mm}-${dd}`, 'YYYY-MM-DD', true).isValid();

	if (!isValidDate) {
		return Promise.reject(new Error('Invalid date in IC number'));
	}

	return Promise.resolve();
};

export const convertKeysToSnakeCase = (obj: Record<string, any>): Record<string, any> => {
	const toSnakeCase = (str: string) => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

	if (Array.isArray(obj)) {
		return obj.map(convertKeysToSnakeCase) as any;
	} else if (obj !== null && typeof obj === 'object') {
		return Object.fromEntries(
			Object.entries(obj).map(([key, value]) => [toSnakeCase(key), convertKeysToSnakeCase(value)])
		);
	}
	return obj;
};

export const convertKeysToCamelCase = (obj: JSONValue): JSONValue => {
	const toCamelCase = (str: string) =>
		str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

	if (Array.isArray(obj)) {
		return obj.map(convertKeysToCamelCase);
	} else if (obj !== null && typeof obj === 'object') {
		return Object.fromEntries(
			Object.entries(obj).map(([key, value]) => [toCamelCase(key), convertKeysToCamelCase(value)])
		);
	}
	return obj;
};

export const linkProxy = async (
	link: string,
	postData?: any,
	transactionId?: string,
	entryId?: string
) => {
	if (postData && typeof postData === 'object') {
		postData = { ...postData };
		postData['app_id'] = ProxyLink.AppId;
		postData['entry_id'] = ProxyLink.EntryId;
		if (transactionId) postData['transaction_id'] = transactionId;
		if (entryId) postData['entry_id'] = entryId;
	}

	try {
		const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}${link}`, postData, {
			headers: {
				'Content-Type': 'application/json',
			},
		});
		return response;
	} catch (error: any) {
		if (isAxiosError(error)) {
			if (error.response?.status === 401) {
				throw new Error('Unauthorized! Invalid credentials.');
			}
		}
		throw error; // re-throw other errors
	}
};
